export const BASE_URL = process.env.REACT_APP_BASE_URL;

const API_URLS = {
  // Publicly accessible endpoints
  gender: `${BASE_URL}/api/core/genders/`,
  country: `${BASE_URL}/api/core/countries/`,
  nationality: `${BASE_URL}/api/core/countries/`,
  programType: `${BASE_URL}/api/nurseries/program-types/`,
  createNurseryWithOwner: `${BASE_URL}/api/nurseries/create-nursery-with-owner/`,

  // For dashboard APIs
  getStatsCount: (nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/dashboard/stats/`,
  getAttendanceDataset: (nurseryId, roomId, startDate, endDate) => `${BASE_URL}/api/nurseries/${nurseryId}/dashboard/attendance/?start_date=${startDate}&end_date=${endDate}&room_id=${roomId}`,
  getGeneralAttendanceDataset: (nurseryId, startDate, endDate) => `${BASE_URL}/api/nurseries/${nurseryId}/dashboard/attendance-general/?start_date=${startDate}&end_date=${endDate}`,
  getBirthdays: (nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/dashboard/birthdays/`,
  getWorkiversaries: (nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/dashboard/anniversaries/`,
  getEvents: (nurseryId, date) => `${BASE_URL}/api/nurseries/${nurseryId}/dashboard/events/?date=${date}`,
  getLastMessages: (nurseryId) => `${BASE_URL}/en/api/manage/messages/lastfour/?nursery_id=${nurseryId}`,
  getLastFourMessages: (nurseryId) => `${BASE_URL}/api/chat/nursery/${nurseryId}/messages/last-unread-or-last/?size=4`,

  getSchoolRooms: (nurseryId, detailed = false) =>
    `${BASE_URL}/api/nurseries/${nurseryId}/classrooms/${detailed ? "?detailed=true" : ""}`,
  getRoomStaff: (roomId) => `${BASE_URL}/en/api/room/${roomId}/staff/`,
  // V1 endpoint, no longer exists in V2 backend — kept only for reference:
  // getRoomStudents: (roomId) => `${BASE_URL}/en/api/room-students/?room_id=${roomId}`,
  // NOTE: pageSize defaults to 1 to preserve the existing "cheap count via
  // pagination metadata" trick used on the Rooms page (fetches total_items
  // without paying for the full page). Callers that actually need the list
  // of students (e.g. a room-scoped student dropdown) should pass an
  // explicit larger pageSize.
  getStudentsByRoom: (nurseryId, roomId, pageSize = 1) =>
    `${BASE_URL}/api/nurseries/${nurseryId}/students/?room_id=${roomId}&page_size=${pageSize}`,
  getIndividualReport: ({ roomId, staffId, studentId, startDate, endDate }) => {
    if (staffId) {
      return `${BASE_URL}/en/api/nursery/individual/?room_id=${roomId}&staff_id=${staffId}&student_id=${studentId}&start_date=${startDate}&end_date=${endDate}`;
    } else {
      return `${BASE_URL}/en/api/nursery/individual/?room_id=${roomId}&student_id=${studentId}&start_date=${startDate}&end_date=${endDate}`;
    }
  },
  getComparativeReport: ({ roomId, staffId, startDate, endDate }) => {
    if (staffId) {
      return `${BASE_URL}/en/api/nursery/comaprison/?room_id=${roomId}&staff_id=${staffId}&start_date=${startDate}&end_date=${endDate}`;
    } else {
      return `${BASE_URL}/en/api/nursery/comaprison/?room_id=${roomId}&start_date=${startDate}&end_date=${endDate}`;
    }
  },

  // V2: the list is scoped by the backend to the logged-in user (owner/staff/parent), returns a flat array
  getSchools: () => `${BASE_URL}/api/nurseries/nurseries/`,
  getSchool: (nurseryId) => `${BASE_URL}/api/nurseries/nurseries/${nurseryId}/`,
  createSchool: () => `${BASE_URL}/api/nurseries/nurseries/`,
  getOwnerMe: () => `${BASE_URL}/api/nurseries/owners/me/`,
  // CONFIRMED (2026-08-16 console error + live sample): this endpoint
  // returns a PAGINATED object — { links, total_items, total_pages,
  // current_page, results: [...] } — not a flat array, regardless of the
  // `detailed` flag. Several call sites (StudentAttendance.js,
  // IndividualReport.js, and possibly others still using
  // `response.data.map(...)` on this) assume a flat array and will throw
  // "data.map is not a function". Always read `response.data.results`.
  getRooms: (userId, nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/classrooms/`,
  getStudentsByClass: (classId) => `${BASE_URL}/en/api/nursery/classes/${classId}/students/`,
  getParents: (userId, nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/parents/`,
  addParent: (nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/parents/`,
  getParent: (nurseryId, parentId) => `${BASE_URL}/api/nurseries/${nurseryId}/parents/${parentId}/`,
  deleteParent: (nurseryId, parentId) => `${BASE_URL}/api/nurseries/${nurseryId}/parents/${parentId}/`,
  updateParent: (nurseryId, parentId) => `${BASE_URL}/api/nurseries/${nurseryId}/parents/${parentId}/`,
  // Staff (v2)
  getStaff: (nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/staff/`,
  getRoomStaff2: (nurseryId, roomId) => `${BASE_URL}/api/nurseries/${nurseryId}/staff/?room_id=${roomId}`,
  createStaff: (nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/staff/`,
  getStaffDetails: (nurseryId, staffId) => `${BASE_URL}/api/nurseries/${nurseryId}/staff/${staffId}/`,
  staffStatusChoices: () => `${BASE_URL}/api/nurseries/staff/status-choices/`,
  checkStaffEmail: (nurseryId, email) => `${BASE_URL}/api/nurseries/${nurseryId}/staff/check/?email=${encodeURIComponent(email)}`,
  linkStaffToNursery: (nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/staff/link/`,
  getMyStaffData: (nurseryId) => `${BASE_URL}/api/nurseries/staff/me/?nursery_id=${nurseryId}`,
  getStudents: (userId, nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/students/brief/`,
  addStudents: (nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/students/`,
  getStudentsAttendance: (nurseryId, date, room_id) => `${BASE_URL}/api/nursery/checkin_set/monthly-attendance?nursery_id=${nurseryId}&month=${date}&room_id=${room_id}`,
  updateStudentContacts: (nurseryId, studentId, contactId) => `${BASE_URL}/api/nurseries/${nurseryId}/students/${studentId}/contacts/${contactId}/`,
  getStudent: (studentId, nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/students/${studentId}/`,
  deleteStudent: (studentId, nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/students/${studentId}/`,
  getStudentContacts: (studentId, nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/students/${studentId}/contacts/`,

  getActivityTypes: (nurseryId) => `${BASE_URL}/api/activities/nursery/${nurseryId}/activitytypes/`,

  // Custom types are the tags a nursery defines for skill / custom / observation /
  // subjectshomework activities (e.g. Skill "Sharing", Homework subject "Math").
  // Pass activityType to filter the list down to tags valid for that activity type.
  // NOTE: URL shapes below match what's confirmed working end-to-end in the Flutter
  // app (activity_repo.dart / app_constants.dart CUSTOM_TITLES_URI) — GET has no
  // trailing slash before the query string, POST does.
  getCustomTypes: (nurseryId, activityType) => {
    const params = activityType ? `?activity_type=${encodeURIComponent(activityType)}` : "";
    return `${BASE_URL}/api/activities/nursery/${nurseryId}/custom-types${params}`;
  },
  createCustomType: (nurseryId) => `${BASE_URL}/api/activities/nursery/${nurseryId}/custom-types/`,

  // Mirrors ActivityRepo.getStudentActivites in the Flutter app, which is
  // confirmed working end-to-end. Two things were wrong in the previous
  // version of this function, which is why the React feed was silently
  // coming back empty:
  //   1. The activity type is a URL PATH SEGMENT, not a `?activity_type=`
  //      query param. "All" activities use the literal segment 'feed';
  //      everything else uses the type's own url/name (e.g. 'skill').
  //      There is no confirmed backend support for `?activity_type=all` —
  //      that query param was never validated against a real payload.
  //   2. Pagination is page-number based (page / page_size), not cursor
  //      based — the Flutter app never sends a cursor param.
  getFeed: (nurseryId, studentId, activityType, { page = 1, pageSize = 30, startDate, endDate } = {}) => {
    const segment = !activityType || activityType === "all" ? "feed" : activityType;
    const params = new URLSearchParams({ page, page_size: pageSize });
    // NOTE: start_date/end_date filtering is NOT present in the Flutter app,
    // so it hasn't been confirmed against a real backend response the way
    // the rest of this function now is. Left in since DRF typically ignores
    // unrecognized query params rather than erroring, but flag this to
    // backend/QA if date filtering on the feed doesn't actually narrow results.
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    return `${BASE_URL}/api/activities/nursery/${nurseryId}/student/${studentId}/${segment}/?${params.toString()}`;
  },

  // ---------------------------------------------------------------------
  // V2 Attendance & Quran Report endpoints
  // Confirmed against Sabeqoon_V2_postman_collection.json (Activities >
  // Attendance / Reports folders). Anything marked UNCONFIRMED has a
  // confirmed URL + params but no saved example response in the
  // collection, so the field names the UI maps over are still guesses —
  // check against a live response or the Flutter equivalent before
  // trusting the table mappings.
  // ---------------------------------------------------------------------

  // Student monthly attendance (was V1 `/en/api/student-attendance-by-month/`
  // and V1 `/api/nursery/checkin_set/monthly-attendance` used by the
  // StudentAttendance grid page — both now 404).
  // CONFIRMED against a live response (2026-08-16): this endpoint returns
  // ALL students for the nursery in one shot, each with a per-day status
  // grid — { last_day, students: [{ id, name, img, attendance: [{status, date}] }] } —
  // not a single student's checkin/checkout log like V1 was. There is no
  // `student_id` filter; per-student screens pick their student out of
  // `students` client-side.
  // `month` and `room_id` query params below are UNCONFIRMED — the live
  // sample was fetched with no query string at all and returned the
  // current month, all rooms, up to today's date. If the backend ignores
  // either, the corresponding picker in the UI won't actually narrow the
  // data — flag to backend/QA. Only params with a value are appended.
  getStudentMonthlyAttendance: (nurseryId, month, roomId) => {
    const params = new URLSearchParams();
    if (month) params.set("month", month);
    if (roomId) params.set("room_id", roomId);
    const qs = params.toString();
    return `${BASE_URL}/api/activities/nursery/${nurseryId}/studentattendance/monthly-attendance/${qs ? `?${qs}` : ""}`;
  },

  // Staff "all" attendance for a given day (was V1 `/en/staff/all-attendance/`)
  // CONFIRMED (2026-08-17) live response shape:
  //   { date, nursery: "<nursery name string>", attendance: [] }
  // `date` is REQUIRED — omitting it 400s with { "error": "date is required" }.
  // The `attendance` array was empty in the live sample (no staff checked in
  // that day), so the shape of an individual record inside it is still
  // UNCONFIRMED. StaffAllAttendance.js maps records defensively with
  // fallback field names — verify against a populated day and simplify the
  // mapping once confirmed.
  getStaffAllAttendance: (nurseryId, date) =>
    `${BASE_URL}/api/activities/nursery/${nurseryId}/staffattendance/all/?date=${date}`,

  // Staff INDIVIDUAL attendance (was V1 `/en/staff/individual-attendance/`)
  // CONFIRMED (2026-08-17): returns a FLAT array (not paginated) of
  // { date, checkin, checkout, total_hours } — one entry per day of the
  // given month. Query params are `staff_id`, `month` (zero-padded, e.g.
  // "08"), and `year` (e.g. "2026") — three separate params, not a
  // combined "YYYY-MM" like the student attendance endpoint takes.
  getStaffIndividualAttendance: (nurseryId, staffId, month, year) =>
    `${BASE_URL}/api/activities/nursery/${nurseryId}/staffattendance/?staff_id=${staffId}&month=${month}&year=${year}`,

  // Quran report — individual student report.
  // V1 had ONE endpoint (`/en/api/nursery/individual/`) returning a combined
  // payload with a `recitation_records` array containing nested
  // memorization/revision objects (PascalCase fields like NewSuraFrom,
  // RevisionSuraFrom, etc.).
  // V2 SPLITS this into 5 separate, paginated endpoints — CONFIRMED against
  // live responses (2026-08-16):
  //   - report/summary/            -> { age, memorization_sura, memorization_ayat,
  //                                      revision_sura, revision_ayat,
  //                                      current_memorization, current_memorization_ar,
  //                                      current_revision, current_revision_ar }
  //                                      NOTE: no score/average fields anymore — V1's
  //                                      memorization_avg/revision_avg have no V2
  //                                      equivalent yet. Backend follow-up needed if
  //                                      the "Performance Summary" averages are still
  //                                      wanted.
  //   - report/homework/memorization/  -> paginated { results: [{ activity_date, juz,
  //                                      juz_mul, sura, sura_ar, sura_en, sura_number,
  //                                      aya_from, aya_to, sura_mul, sura_mul_ar,
  //                                      sura_mul_en, sura_mul_numbers, note }] }
  //   - report/homework/revision/      -> same shape, `juz_revision`/`juz_mul_revision`
  //                                      instead of `juz`/`juz_mul`
  //   - report/recitation/memorization/ and report/recitation/revision/ -> same shape
  //                                      as homework PLUS `mistakes_telawa`,
  //                                      `mistakes_tagweed`, `score`
  // Homework and recitation records are genuinely different record types now
  // (homework has no score/mistakes) — the UI renders them as two separate
  // tables instead of V1's single merged "recitation_records" list.
  getQuranReportSummary: (nurseryId, studentId, startDate, endDate) =>
    `${BASE_URL}/api/activities/nursery/${nurseryId}/report/summary/?student_id=${studentId}&start_date=${startDate}&end_date=${endDate}`,
  getQuranHomeworkMemorization: (nurseryId, studentId, startDate, endDate, page = 1, pageSize = 8) =>
    `${BASE_URL}/api/activities/nursery/${nurseryId}/report/homework/memorization/?student_id=${studentId}&start_date=${startDate}&end_date=${endDate}&page=${page}&page_size=${pageSize}`,
  getQuranHomeworkRevision: (nurseryId, studentId, startDate, endDate, page = 1, pageSize = 8) =>
    `${BASE_URL}/api/activities/nursery/${nurseryId}/report/homework/revision/?student_id=${studentId}&start_date=${startDate}&end_date=${endDate}&page=${page}&page_size=${pageSize}`,
  getQuranRecitationMemorization: (nurseryId, studentId, startDate, endDate, page = 1, pageSize = 8) =>
    `${BASE_URL}/api/activities/nursery/${nurseryId}/report/recitation/memorization/?student_id=${studentId}&start_date=${startDate}&end_date=${endDate}&page=${page}&page_size=${pageSize}`,
  getQuranRecitationRevision: (nurseryId, studentId, startDate, endDate, page = 1, pageSize = 8) =>
    `${BASE_URL}/api/activities/nursery/${nurseryId}/report/recitation/revision/?student_id=${studentId}&start_date=${startDate}&end_date=${endDate}&page=${page}&page_size=${pageSize}`,

  // Quran juz lookup — for resolving juz/sura numeric IDs to display names
  // (same pattern as the customTypesById cache used in the activity feed).
  // URL confirmed; response shape unconfirmed (no saved example).
  getQuranJuz: (nurseryId) => `${BASE_URL}/api/activities/nursery/${nurseryId}/quran-juz/`,

  // Comparative Quran report (was V1 `/en/api/nursery/comaprison/`)
  // ** NO V2 ENDPOINT EXISTS. ** There is no "comparison"-equivalent
  // anywhere in the V2 Postman collection. Confirmed backend gap — the
  // Comparative Report tab has nothing to call in V2 yet.
  // getComparativeReport: ({ roomId, staffId, startDate, endDate }) => ...
  room: `${BASE_URL}/en/api/nursery/classes`,
  student: `${BASE_URL}/api/nurseries/students`,
  parent: `${BASE_URL}/en/api/nursery/parents`,
  staff_attendance_check: (nurseryId) => `${BASE_URL}/api/manage/attendance_check/?nursery_id=${nurseryId}`,
  student_attendance_check: () => `${BASE_URL}/api/nursery/student-checkin/`,

  // For messages page
  getAdminInbox: (nurseryId, groupType, page) => `${BASE_URL}/api/chat/nursery/${nurseryId}/chat-inbox-admin/?group_type=${groupType}&page=${page}`,
  getGroupMessages: (nurseryId, groupName, cursor) => {
    let url = `${BASE_URL}/api/chat/nursery/${nurseryId}/chat-group/${groupName}/messages/?page_size=200`;
    if (cursor) {
      url = `${url}&cursor=${cursor}`;
    }
    return url;
  },
  sendGroupMessage: (nurseryId, groupName) => `${BASE_URL}/api/chat/nursery/${nurseryId}/chat-group/${groupName}/send-message/`,
  markGroupAsRead: (nurseryId, groupName) => `${BASE_URL}/api/chat/nursery/${nurseryId}/chat-group/${groupName}/mark-read/`,

  // For calendar page
  events: (nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/events/`,
  rangeOfEvents: (nurseryId) => `${BASE_URL}/api/nurseries/${nurseryId}/events/range/`,

  // Admission Form Links
  getAdmissionLinks: (nurseryId) => `${BASE_URL}/api/admission-form/links/?nursery_id=${nurseryId}`,
  generateAdmissionLink: () => 
    `${BASE_URL}/api/admission-form/links/`,
  toggleLinkStatus: (linkId, action) => 
    `${BASE_URL}/api/admission-form/links/${linkId}/${action}/`,
  deleteAdmissionLink: (linkId) => 
    `${BASE_URL}/api/admission-form/links/${linkId}/`,

  validateLink: (token) => `${BASE_URL}/api/admission-form/validate/${token}/`,
  fetchAdmissionMetaData: (token) => `${BASE_URL}/api/admission-form/metadata/${token}/`,
  checkParentEmail: (email) => `${BASE_URL}/api/admission-form/check-parent/?email=${encodeURIComponent(email)}`, 
  sendAdmissionOTP: () => `${BASE_URL}/api/admission-form/request-otp/`,
  verifyAdmissionOTP: () => `${BASE_URL}/api/admission-form/verify-otp/`,
  getMyChildrenForAdmission: (linkToken) => `${BASE_URL}/api/admission-form/my-children/?link_token=${linkToken}`,
  submitForm: () => `${BASE_URL}/api/admission-form/submit/`,

  getApplicationsOverview: (nurseryId , page , pageSize ) => 
    `${BASE_URL}/api/admission-form/applications/children-overview/?nursery_id=${nurseryId}&page=${page}&page_size=${pageSize}`,
  approveApplication: (childId) =>
    `${BASE_URL}/api/admission-form/child/${childId}/approve/`,
  rejectApplication: (childId) =>
    `${BASE_URL}/api/admission-form/child/${childId}/reject/`,

  updateAdmissionApplicationChild: (childId) =>
    `${BASE_URL}/api/admission-form/child/${childId}/`,
  updateAdmissionApplicationParent: (childId, parentNumber) =>
    `${BASE_URL}/api/admission-form/child/${childId}/parent${parentNumber}/`,
  updateAdmissionApplicationContact: (childId, contactId) =>
    `${BASE_URL}/api/admission-form/child/${childId}/contact/${contactId}/`,


  getApplictionChildDetails: (childId) =>
    `${BASE_URL}/api/admission-form/child/${childId}/full-details/`,

  // For Documents Management APIs
  createDocumentGroup: () =>
     `${BASE_URL}/api/documents/`,
  addFilesToDocumentGroup: (groupId) => 
    `${BASE_URL}/api/documents/${groupId}/add-files/`,
  getDocumentGroups: (nurseryId, target_audience, room, page = 1) =>
     `${BASE_URL}/api/documents/?nursery=${nurseryId}&target_audience=${target_audience}&room=${room}&page=${page}`,
  getDocumentGroupDetails: (groupId) =>
    `${BASE_URL}/api/documents/${groupId}/`,
  updateDocumentGroup: (groupId) => 
    `${BASE_URL}/api/documents/${groupId}/`,
  deleteDocumentGroup: (groupId) => 
    `${BASE_URL}/api/documents/${groupId}/`,
  removeDocumentFile: (groupId, fileId) =>
     `${BASE_URL}/api/documents/${groupId}/remove-file/${fileId}/`,

  // Parent Invitation API
  resendParentInvitation: (nurseryId, parentId) => `${BASE_URL}/api/nurseries/${nurseryId}/parents/${parentId}/resend-invitation/`,

  // Book a Demo API
  bookDemo: () => 
    `${BASE_URL}/api/nurseries/demo-bookings/`,

  demoBookedSlots: (dateStr) =>
    `${BASE_URL}/api/nurseries/demo-bookings/booked-slots/?date=${dateStr}`,
};

export default API_URLS;